import { type ReactNode, useEffect } from 'react';

import { useLocation } from 'react-router';
import { toast } from 'react-toastify';

import { AuthRedirectController } from '@ll-platform/frontend/core/auth/guards/AuthRedirectController';
import { useUser } from '@ll-platform/frontend/core/auth/hooks/useUser';
import {
  AccountType,
  ExternalAccountTypes,
  InternalAccountTypes,
} from '@ll-platform/frontend/core/auth/types';
import { isUserInternal } from '@ll-platform/frontend/core/auth/utils/isInternal';
import { Pages } from '@ll-platform/frontend/core/router/pages';

type IsLoggedInGuardProps = {
  accountTypes?: AccountType[];
  children?: ReactNode;
  redirectPage?: string;
  emailHint?: string;
  loadingComponent: ReactNode;
};

export const IsLoggedInGuard = ({
  children,
  accountTypes = [],
  redirectPage = Pages.Login,
  emailHint,
  loadingComponent,
}: IsLoggedInGuardProps) => {
  const location = useLocation();
  const { isLoading, isAuthorized, activeUser } = useUser();

  const isAllowed =
    isAuthorized &&
    (activeUser.accountType === AccountType.SuperAdmin ||
      accountTypes.length === 0 ||
      accountTypes.includes(activeUser.accountType));

  // biome-ignore lint/correctness/useExhaustiveDependencies: accountTypes?.join, activeUser, location.pathname
  useEffect(() => {
    if (isAuthorized && !isAllowed) {
      console.warn(
        `User ${activeUser.email} ${activeUser._id} tried to access ${location.pathname} which is only for ${accountTypes?.join(', ')}`,
      );
      toast.error(
        `Only ${isUserInternal(activeUser) ? 'external' : 'internal'} users are allowed to access the page you tried to open`,
      );
    }
  }, [isAuthorized, isAllowed]);

  if (isLoading) {
    return loadingComponent;
  }

  if (!isAllowed) {
    return (
      <AuthRedirectController
        loadingComponent={loadingComponent}
        unauthorizedRedirectPage={redirectPage}
        emailHint={emailHint}
      />
    );
  }

  return children;
};

export const IsInternalLoggedInGuard = (
  props: Omit<IsLoggedInGuardProps, 'accountTypes'>,
) => {
  return <IsLoggedInGuard {...props} accountTypes={InternalAccountTypes} />;
};

export const IsExternalLoggedInGuard = (
  props: Omit<IsLoggedInGuardProps, 'accountTypes'>,
) => {
  return <IsLoggedInGuard {...props} accountTypes={ExternalAccountTypes} />;
};
